import React, { useEffect, useState } from 'react';
import { useTheme } from '@emotion/react';
import {
  Backdrop,
  Dialog,
  Paper,
  useTheme as useMaterialTheme,
  useMediaQuery,
  type BackdropProps,
  type DialogProps,
} from '@mui/material';
import _, { isString } from 'lodash';
import { MobileLayout } from '@bbk/retention-sdk/contracts/layout';
import {
  createModalActionQueue,
  createModalBackwardsQueue,
  type ModalRefundSync,
} from '@bbk/retention-sdk/internal';
import {
  type RestMultiRowSurveyCard,
  type SurveyType,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import { type RestExperience } from '@bbkAdminUtils/api-client/rest-experiences-api';
import {
  RestModalAction,
  RestModalTemplates,
  type RestModalCard,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { OfferPresentation } from '@bbkAdminUtils/api-client/rest-offers-api';
import { cs } from '@bbkAdminUtils/css-utils';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { type ValueOf } from '@bbkAdminUtils/utility-types';
import {
  type ExecuteEventsHandler,
  type NextStepHandler,
} from '@bbkFunnel/events';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import CardFactory from '@bbkFunnelComponents/card-factory';
import { CANCEL_WITH_REFUND_MODAL_ID } from '@bbkFunnelComponents/constants';
import { useLaConfirmation } from '@bbkFunnelComponents/loss/use-la-confirmation';
import { isCheckoutAction } from '@bbkFunnelUtils/cancel-checkout';
import { getModalCtaIdMap } from '@bbkFunnelUtils/cards-utils';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';
import { useMediaQueries } from './modals/sections/media-queries';

export interface ModalActionHandler {
  (
    cta: RestModalForwards,
    extra?:
      | { message?: string; excludeOfferAccepts?: boolean }
      | 'cancel'
      | 'other_actions'
  ): void;
}

export type ModalHandleAnyInfo = (args: {
  modal: string;
  message?: string;
}) => void;

// This is comprehensive enum, but at the moment only 'x' is being sent from the modal.
// TODO: we need to add handling of backdrop, esc and a special type of action: 'dismiss_modal'
export enum FunnelModalOnCloseReasonEnum {
  backdropClick = 'backdropClick',
  escapeKeyDown = 'escapeKeyDown',
  x = 'x',
  dismiss_modal = 'dismiss_modal',
}

export type FunnelModalOnCloseReason =
  FunnelModalOnCloseReasonEnum | ValueOf<FunnelModalOnCloseReasonEnum>;

export type ModalOnClose = (reason?: FunnelModalOnCloseReason) => unknown;

type Props = {
  fullInfo: Pick<RestExperience, 'session' | 'version' | 'layout'>;
  modal: RestModalCard;
  surveyType: SurveyType;
  questions: RestMultiRowSurveyCard['requires']['multi_row_survey'];
  fullSurvey: RestMultiRowSurveyCard;
  response: RestMultiRowSurveyCard['provides'];
  nevermind: ModalOnClose;
  showLoaderOverlay: () => void;
  open?: boolean;
  nextStep: NextStepHandler;
  executeEvents: ExecuteEventsHandler;
  sendLCOEventsToParent?: CallableFunction;
  handleAnyInformation: ModalHandleAnyInfo;
  presentation: string | undefined;

  // Lifecycle
  isLifecycle?: boolean;
};

export const Modal: React.FC<Props> = (props) => {
  // console.log('[Modal Component] props', props);
  const theme = useTheme() as EmotionTheme;
  const materialTheme = useMaterialTheme();
  const {
    fullInfo: {
      session,
      version,
      layout: { cards },
    },
    modal,
    modal: { dismissable: modalDismissable } = {},
    fullInfo,
    response,
    nextStep,
    executeEvents,
    sendLCOEventsToParent,
    nevermind,
    surveyType,
    open,
    questions,
    handleAnyInformation,
    isLifecycle,
    presentation,
  } = props;
  const confirmation = useLaConfirmation({
    fullInfo,
    modal,
    response,
    open,
    executeEvents,
    isLifecycle,
  });
  const [showCode, setShowCode] = useState(false);
  const [refundModalSyncData, setRefundModalSyncData] =
    useState<ModalRefundSync | null>(null);
  const fullScreen = useMediaQuery(materialTheme.breakpoints.down('sm'));

  const { mobileView } = useMediaQueries();

  useEffect(() => {
    if (
      isLifecycle &&
      modal.template === RestModalTemplates.modal_email_success
    ) {
      sendLCOEventsToParent?.({
        events: [
          {
            event: 'resize',
            data: {
              presentation: presentation ?? 'modal',
              template: 'confirmation_modal',
            },
          },
        ],
      });
    }
  }, [isLifecycle, modal, presentation, sendLCOEventsToParent]);

  const onRequestClose: DialogProps['onClose'] = (event, reason) => {
    setShowCode(false);
    if (modalDismissable === false) {
      return;
    }
    if (
      modal.template === RestModalTemplates.final_offer &&
      (reason === 'backdropClick' || reason === 'escapeKeyDown')
    ) {
      return;
    }

    nevermind();
  };

  // Documentation for the modal actions and their format is located in
  // documentation/modal-action-format.md
  // Adding or changing anything? Make sure to do update the documentation
  const handleAction: ModalActionHandler = async (modalAction, extra) => {
    if (confirmation.handleAction(modalAction)) return;
    const modalCtaIdMap = getModalCtaIdMap(cards);
    if (modalAction.cta_details_list?.length) {
      if (!modalAction.cta_details_list[0]?.cta_id) {
        GlobalLogger.error(
          `Modal action (Navigation) exists with a non-zero cta_detail_list, but the ctaID of the first item is either blank or invalid. ${session}`
        );
      } else if (!modalAction.cta_details_list?.[0]?.cta_id) {
        GlobalLogger.error(
          `Modal action (Navigation) exists with a non-zero cta_detail_list and ctaID but the ?.[].? operator is not working for some browsers. ${session}`
        );
      }
    } else {
      GlobalLogger.log(
        `Modal action (Navigation) is null or empty. ${session}`
      );
    }
    const messageVal =
      _.get(extra, 'message') || _.get(modalAction, 'sync.message');
    const queueContext = {
      experience: fullInfo,
      modal,
      action: modalAction,
      survey: response,
      fallbackCtaId: modalCtaIdMap[modal.name],
      message: messageVal,
    };

    if (!isLifecycle && extra !== 'cancel' && isCheckoutAction(modalAction)) {
      await executeEvents(
        createModalActionQueue({ ...queueContext, kind: 'checkout' })
      );
      return;
    }

    if (modalAction.action === RestModalAction.show_code) {
      setShowCode(!showCode);
    } else if (modalAction.action.indexOf(RestModalAction.modal_email) > -1) {
      // Legacy action names still select the modal when no explicit target exists.
      nextStep(modalAction.modal || modalAction.action, extra);
    } else if (modalAction.action === RestModalAction.send_email) {
      handleAnyInformation({ modal: modal.name, message: messageVal });
      // The builder includes this response before React has committed the update.
      executeEvents(
        createModalActionQueue({ ...queueContext, kind: 'send_email' })
      );
      nextStep(modalAction.modal || modalAction.action);
    } else if (
      modalAction.action === RestModalAction.accept_offer ||
      modalAction.action === RestModalAction.accept_offer_with_confirmation
    ) {
      executeEvents(
        createModalActionQueue({ ...queueContext, kind: 'accept_offer' })
      );
      nextStep(modalAction.modal || modalAction.action);
    } else if (
      modalAction.action === RestModalAction.link ||
      modalAction.action === RestModalAction.cancel_with_link
    ) {
      if (
        extra === 'cancel' ||
        modalAction.action === RestModalAction.cancel_with_link
      ) {
        if (refundModalSyncData) setRefundModalSyncData(null);
        executeEvents(
          createModalActionQueue({
            ...queueContext,
            kind: 'cancel',
            refundSync: refundModalSyncData,
          })
        );
      } else {
        executeEvents(
          createModalActionQueue({
            ...queueContext,
            kind: 'link',
            includeOfferAccepts:
              !isString(extra) && !extra?.excludeOfferAccepts,
            isLifecycle,
          })
        );
      }
    } else if (modalAction.action === RestModalAction.dismiss_modal) {
      nevermind();
    } else if (modalAction.action === RestModalAction.confirm_cancel) {
      if (modal.id === CANCEL_WITH_REFUND_MODAL_ID && modalAction.sync) {
        setRefundModalSyncData(modalAction.sync);
      }
      nextStep(modalAction.modal || modalAction.action);
    } else if (modalAction.action === RestModalAction.nevermind) {
      executeEvents(
        createModalActionQueue({ ...queueContext, kind: 'nevermind' })
      );
      return;
    }
  };

  const handleBackwards = (modalAction: RestModalForwards) => {
    executeEvents(
      createModalBackwardsQueue({
        experience: fullInfo,
        modal,
        action: modalAction,
        survey: response,
      })
    );
  };

  const content = (
    <CardFactory
      card={confirmation.card}
      nevermind={() => nevermind('x')}
      response={response}
      questions={questions}
      surveyType={surveyType}
      handleAction={handleAction}
      handleBackwards={handleBackwards}
      showCode={showCode}
    />
  );

  const isBanner = modal.template === RestModalTemplates.lco_banner;
  const isModal = presentation === OfferPresentation.modal;
  const isConfirmationModal =
    modal.template === RestModalTemplates.modal_email_success;

  const mobileLayout = modal?.mobileLayout;

  const specificModalStyles = () => {
    if (!fullScreen) {
      switch (modal.template) {
        case RestModalTemplates.sixty_forty_column:
          return {
            maxHeight: 600,
            height: 'calc(100% - 24px)',
          };
        case RestModalTemplates.video_row:
          return {
            width: '100%',
            maxWidth: 750,
          };
        default:
          return {};
      }
    }
    return {};
  };

  const paperStyle = (): any => {
    switch (modal.template) {
      case RestModalTemplates.lco_banner: {
        const commonStyle = {
          width: '100% !important',
          margin: '0',
          position: 'absolute',
          maxHeight: '100%',
        };
        switch (modal.additional_properties?.position) {
          case 'top': {
            return {
              ...commonStyle,
              top: '0',
            };
          }
          default: {
            return {
              ...commonStyle,
              bottom: '0',
            };
          }
        }
      }
      case RestModalTemplates.lco_popup: {
        return {
          position: 'absolute',
          maxHeight: '100%',
          bottom: '0',
          borderRadius: `${theme.borderRadius}px !important}`,
        };
      }
      case RestModalTemplates.lco_modal || RestModalTemplates.video_row: {
        return {
          borderRadius: `${theme.borderRadius}px !important}`,
        };
      }
      default: {
        return {};
      }
    }
  };

  const WrappedDialog = (props: DialogProps) => (
    <Dialog
      {...props}
      sx={{
        '& .MuiDialog-scrollPaper': {
          backgroundImage: isLifecycle
            ? 'none'
            : 'radial-gradient(50% 136%, rgba(37,37,37,0.50) 51%, rgba(0,0,0,0.75) 100%)',
        },
        '& .MuiPaper-root': {
          fontSize: theme.fontSize,
          fontFamily: theme.fontFamily,
          color: theme.modalTextColor || theme.textColor,
          background: theme.modalBackground,
          ...specificModalStyles(),
        },
        '& .MuiDialog-container': {
          ...(isModal &&
            isLifecycle &&
            !fullScreen && {
              transform: 'scale(0.8)',
            }),
        },
      }}
    />
  );

  const BackdropComponent = (props: BackdropProps) => (
    <Backdrop
      {...props}
      sx={{
        backgroundColor:
          isLifecycle && !isModal ? 'transparent' : 'rgba(0, 0, 0, 0.5)',
      }}
    />
  );

  const LifecycleDialog = (props: DialogProps) => (
    <Dialog
      {...props}
      sx={{
        '& .MuiDialog-container': {
          ...(isModal && {
            transform: 'scale(0.8)',
          }),
        },
        '& .MuiPaper-root': {
          margin: '0',
          fontSize: theme.fontSize,
          fontFamily: theme.fontFamily,
          color: theme.modalTextColor || theme.textColor,
          background: theme.modalBackground,
          border: '0px !important',
          lineHeight: 1.5,
          ...paperStyle(),
        },
        '& .MuiDialog-paperWidthSm': {
          ...(isBanner && {
            maxWidth: '100% !important',
          }),
        },
        '& .MuiDialog-paperScrollPaper': {
          ...(isBanner && {
            maxHeight: '70px',
          }),
        },
      }}
    />
  );
  // it renders popup and banners
  if (isLifecycle && !isConfirmationModal && !isModal) {
    return (
      <LifecycleDialog
        open={Boolean(open)}
        slots={{
          backdrop: isModal ? Backdrop : BackdropComponent,
        }}
        maxWidth={isBanner ? false : 'md'}
        style={{ height: '100%', boxShadow: 'none' }}
        PaperComponent={(props) => (
          <Paper
            variant="outlined"
            {...props}
            style={{
              ...props.style,
              ...(isBanner && {
                width: '100%',
                maxWidth: '100%',
                margin: 0,
              }),
            }}
          />
        )}
      >
        {content}
      </LifecycleDialog>
    );
  }

  // it renders all modals templates
  return (
    <WrappedDialog
      open={Boolean(open)}
      onClose={onRequestClose}
      className="BrightBox"
      maxWidth="md"
      fullScreen={fullScreen}
      style={
        mobileLayout &&
        mobileView &&
        mobileLayout === MobileLayout.PARTIAL_MODAL
          ? { position: 'sticky' }
          : undefined
      }
      PaperComponent={(props) => (
        <Paper
          {...props}
          className={cs(
            props.className,
            modal.template,
            makeClassNameSafe(modal.name)
          )}
        />
      )}
    >
      {content}
    </WrappedDialog>
  );
};
